import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tour, TourPlatform } from '@/types/tour';

export function useTours() {
  return useQuery({
    queryKey: ['tours'],
    queryFn: async (): Promise<Tour[]> => {
      const { data: tours, error } = await supabase
        .from('tours')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;

      const { data: platforms, error: pError } = await supabase
        .from('tour_platforms')
        .select('*');

      if (pError) throw pError;

      return (tours as Tour[]).map(t => ({
        ...t,
        tour_platforms: (platforms as TourPlatform[]).filter(p => p.tour_id === t.id),
      }));
    },
  });
}

export function useAdminTours() {
  return useQuery({
    queryKey: ['admin-tours'],
    queryFn: async (): Promise<Tour[]> => {
      const { data: tours, error } = await supabase
        .from('tours')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;

      const { data: platforms, error: pError } = await supabase
        .from('tour_platforms')
        .select('*');

      if (pError) throw pError;

      // Fetch click counts per platform
      const { data: clicks, error: cError } = await supabase
        .from('platform_clicks')
        .select('platform_id');

      const clickCounts: Record<string, number> = {};
      if (!cError && clicks) {
        clicks.forEach((c: any) => {
          clickCounts[c.platform_id] = (clickCounts[c.platform_id] || 0) + 1;
        });
      }

      return (tours as Tour[]).map(t => ({
        ...t,
        tour_platforms: (platforms as TourPlatform[]).filter(p => p.tour_id === t.id).map(p => ({
          ...p,
          click_count: clickCounts[p.id] || 0,
        })),
      }));
    },
  });
}

export function useDeleteTour() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('tours').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-tours'] }),
  });
}

export function useToggleTourActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase.from('tours').update({ is_active }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-tours'] });
      qc.invalidateQueries({ queryKey: ['tours'] });
    },
  });
}

export function useSaveTour() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tour,
      platforms,
      coverFile,
    }: {
      tour: Partial<Tour> & { title: string };
      platforms: Omit<TourPlatform, 'id' | 'tour_id' | 'created_at' | 'updated_at'>[];
      coverFile?: File | null;
    }) => {
      let cover_photo_url = tour.cover_photo_url || null;

      if (coverFile) {
        const ext = coverFile.name.split('.').pop();
        const path = `${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('tour-covers')
          .upload(path, coverFile);
        if (upErr) throw upErr;
        const { data: urlData } = supabase.storage.from('tour-covers').getPublicUrl(path);
        cover_photo_url = urlData.publicUrl;
      }

      const tourData = {
        title: tour.title,
        short_description: tour.short_description || null,
        inclusions: tour.inclusions || null,
        itinerary: tour.itinerary || null,
        reviews_summary: tour.reviews_summary || null,
        cover_photo_url,
        company_name: tour.company_name || null,
        available_to: (tour as any).available_to || null,
        departure_point: tour.departure_point || null,
        duration_hours: tour.duration_hours ?? null,
        pick_up: tour.pick_up || null,
        boat_type: tour.boat_type || null,
        categories: tour.categories || null,
        time_of_day: (tour as any).time_of_day || null,
        vibe: tour.vibe || null,
        crowd_size: tour.crowd_size || null,
        snorkeling_quality: tour.snorkeling_quality || null,
        food_quality: tour.food_quality || null,
        open_bar: tour.open_bar || null,
        crew: (tour as any).crew || null,
        boarding: (tour as any).boarding || null,
        best_for: (tour as any).best_for || null,
        pros: tour.pros || null,
        cons: tour.cons || null,
        sort_order: tour.sort_order ?? 0,
        is_active: tour.is_active ?? true,
      };

      let tourId = tour.id;

      if (tourId) {
        const { error } = await supabase.from('tours').update(tourData).eq('id', tourId);
        if (error) throw error;
        // Delete existing platforms and re-insert
        await supabase.from('tour_platforms').delete().eq('tour_id', tourId);
      } else {
        const { data, error } = await supabase.from('tours').insert(tourData).select('id').single();
        if (error) throw error;
        tourId = data.id;
      }

      if (platforms.length > 0) {
        const { error } = await supabase.from('tour_platforms').insert(
          platforms.map(p => ({ ...p, tour_id: tourId }))
        );
        if (error) throw error;
      }

      return tourId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-tours'] });
      qc.invalidateQueries({ queryKey: ['tours'] });
    },
  });
}
